import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";

import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import {
  FaFileAlt,
  FaFileSignature,
  FaShieldAlt,
  FaMoneyBill,
  FaTrash,
  FaUpload,
  FaInfoCircle,
  FaCloudUploadAlt,
  FaTimes,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import styled from "styled-components";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  getFirestore,
  collection,
  setDoc,
  doc,
  getDoc,
  addDoc,
  serverTimestamp,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import sendCustomEmail from "../../functions/sendCustomEmail";
import { getAuth } from "firebase/auth";
import Swal from "sweetalert2";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: ${(props) =>
    props.type === "sign"
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : "linear-gradient(135deg, #198754 0%, #157347 100%)"};
  color: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
`;

function Onboarding({ usuario }) {
  const [selectedFolder, setSelectedFolder] = useState("");
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [ruc, setRuc] = useState(usuario.ruc);
  const [nombre, setNombre] = useState(usuario.nombre);
  const [apellidos, setApellidos] = useState(usuario.apellidos);
  const [email, setEmail] = useState(usuario.email);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Cargar documentos de tipo firma desde la lista maestra
  useEffect(() => {
    const cargarDocumentosDeFirma = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const rucEmpresa = usuario.ruc || "20538856674";

        // Referencia a la colección de documentos en la lista maestra
        const documentosRef = collection(
          db,
          `onboarding_lista/${rucEmpresa}/documentos`
        );
        const documentosSnap = await getDocs(documentosRef);

        console.log(
          "📊 Total de documentos en Firestore:",
          documentosSnap.size
        );

        // Procesar los documentos y filtrar solo los de tipo "sign"
        const documentosFirma = [];

        documentosSnap.forEach((docSnapshot) => {
          const docData = docSnapshot.data();

          console.log("📄 Documento procesado:", {
            id: docSnapshot.id,
            title: docData.title,
            type: docData.type,
            activo: docData.activo,
            obligatorio: docData.obligatorio,
          });

          // Solo incluir documentos de tipo "sign" y que estén activos
          if (docData.type === "sign" && docData.activo === true) {
            documentosFirma.push({
              id: docSnapshot.id, // Usar el ID real del documento de Firestore
              name: docData.title,
              icon: "document",
              type: "sign",
              description: `Documento para firma: ${docData.title}`,
              originalId: docSnapshot.id,
              obligatorio: docData.obligatorio || false,
            });
          }
        });

        // Ordenar por ID para mantener consistencia
        documentosFirma.sort((a, b) => {
          const idA = parseInt(a.id) || 0;
          const idB = parseInt(b.id) || 0;
          return idA - idB;
        });

        console.log(
          "✅ Documentos de tipo 'sign' y activos encontrados:",
          documentosFirma.length
        );
        console.log("📋 Lista de documentos de firma:", documentosFirma);

        // Verificar si hay documentos de tipo sign pero inactivos
        const documentosSignInactivos = [];
        documentosSnap.forEach((docSnapshot) => {
          const docData = docSnapshot.data();
          if (docData.type === "sign" && docData.activo !== true) {
            documentosSignInactivos.push({
              id: docSnapshot.id,
              title: docData.title,
              activo: docData.activo,
            });
          }
        });

        if (documentosSignInactivos.length > 0) {
          console.log(
            "⚠️ Documentos de tipo 'sign' pero inactivos:",
            documentosSignInactivos
          );
        }

        if (documentosFirma.length === 0) {
          setLoadError(
            "No se encontraron documentos de firma activos en la lista maestra"
          );
        } else {
          setFolders(documentosFirma);
        }
      } catch (error) {
        console.error("Error al cargar documentos de firma:", error);
        setLoadError("Error al cargar los documentos: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    cargarDocumentosDeFirma();
  }, [usuario.ruc]);

  const getIcon = (iconName) => {
    switch (iconName) {
      case "document-text":
        return <FaFileAlt />;
      case "document":
        return <FaFileSignature />;
      case "shield-checkmark":
        return <FaShieldAlt />;
      case "cash":
        return <FaMoneyBill />;
      default:
        return <FaFileAlt />;
    }
  };

  const validateFileName = (fileName) => {
    // Remover la extensión .pdf
    const nameWithoutExtension = fileName.replace(".pdf", "");

    // Verificar si comienza con 8-9 dígitos seguidos de un guion
    const dniPattern = /^(\d{8,9})-(.+)$/;
    const match = nameWithoutExtension.match(dniPattern);

    if (!match) {
      return false;
    }

    return {
      dni: match[1],
      titulo: match[2].trim().toUpperCase(),
    };
  };

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    const newErrors = [];
    const validFiles = [];

    newFiles.forEach((file) => {
      const validationResult = validateFileName(file.name);
      if (!validationResult) {
        newErrors.push(
          `${file.name}: El nombre del archivo debe tener el formato DNI-TITULO.pdf (ejemplo: 41607220-Contrato de trabajo.pdf)`
        );
      } else {
        // Agregar la información validada al objeto file
        file.dniInfo = validationResult;
        validFiles.push(file);
      }
    });

    setFiles(validFiles);
    setErrors(newErrors);
  };

  const removeFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const getUserData = async (dni) => {
    try {
      const userRef = doc(db, "users", dni);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        console.log(`Datos encontrados para DNI ${dni}:`, userData);
        return userData;
      } else {
        // Registrar el error pero continuar con el proceso
        console.warn(`No se encontró usuario con DNI: ${dni}`);
        return {
          name: "Usuario",
          apellidos: dni,
          email: email,
          dni: dni,
        };
      }
    } catch (error) {
      console.error(`Error al obtener datos del usuario ${dni}:`, error);
      // Retornar datos por defecto para no detener el proceso
      return {
        name: "Usuario",
        apellidos: dni,
        email: email,
        dni: dni,
      };
    }
  };

  const handleUpload = async () => {
    if (!selectedFolder) {
      setErrors(["Por favor, seleccione un tipo de documento primero"]);
      return;
    }
    if (files.length === 0) {
      setErrors(["Por favor, seleccione al menos un archivo"]);
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      const storage = getStorage(firebaseApp);
      const db = getFirestore(firebaseApp);
      const selectedDoc = folders.find((f) => f.id === selectedFolder);
      const timestamp = new Date().getTime();
      const currentDate = new Date();

      const nombreEmpresa =
        usuario.empresaNombreComercial || usuario.empresa || "";

      if (!nombreEmpresa) {
        console.warn(
          "⚠️ No se encontró nombre de empresa en los datos del usuario"
        );
      } else {
        console.log("✅ Nombre de empresa encontrado:", nombreEmpresa);
      }

      const totalFiles = files.length;
      let completedFiles = 0;

      const uploadPromises = files.map(async (file) => {
        const { dni, titulo } = file.dniInfo;
        const newFileName = `${dni}-${titulo}.pdf`;

        try {
          // Obtener los datos del usuario
          const userData = await getUserData(dni);

          const storageRef = ref(
            storage,
            `onboarding/${dni}/${selectedDoc.name}/${newFileName}`
          );

          // Subir archivo a Storage
          const uploadResult = await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(uploadResult.ref);

          const nombreEmpresa =
            usuario.empresaNombreComercial || usuario.empresa || "";
          const correo = userData.email;
          const cc = userData.recursosHumanos;
          const bcc = "miboleta@perubi.com";
          const asunto = `${nombreEmpresa} - ${titulo} para vuestra firma`;
          const body =
            `Estimado(a) ${userData.name} ${userData.apellidos},\n\n` +
            `Se le ha asignado un nuevo documento ${titulo} para su firma.\n` +
            `Por favor ingrese al aplicativo Mi Boleta Gestión de Onboarding para revisarlo.\n\n` +
            `Saludos cordiales,\n${nombreEmpresa}`;
          const colecion = "mailTurbo";

          // Si el usuario no existe en Firestore, enviar correo al usuario logueado
          if (
            !userData ||
            (userData.name === "Usuario" && userData.apellidos === dni)
          ) {
            const asuntoAviso = `Onboarding: Trabajador no registrado (${dni})`;
            const bodyAviso =
              `Hola ${usuario.nombre || usuario.email},\n\n` +
              `Intentaste enviar el documento "${titulo}" al trabajador con DNI ${dni}, pero no está registrado en el sistema.\n` +
              `Por favor verifica el DNI o registra al trabajador antes de enviar documentos para firma.\n\n` +
              `Saludos,\nMi Boleta`;

            await sendCustomEmail(
              usuario.email,
              null,
              bcc,
              asuntoAviso,
              bodyAviso,
              colecion
            );
          } else {
            // Enviar correo al trabajador normalmente
            await sendCustomEmail(correo, cc, bcc, asunto, body, colecion);
          }

          // --- NUEVO: Asignar automáticamente el tipo de documento al usuario ---
          // Esto asegura que el usuario pueda ver el documento en su app incluso si el admin olvidó asignarlo
          const docAssignmentRef = doc(
            db,
            `onboarding/${dni}/documentos`,
            selectedDoc.id
          );

          await setDoc(
            docAssignmentRef,
            {
              id: selectedDoc.id,
              title: selectedDoc.name,
              type: selectedDoc.type || "upload",
              obligatorio: selectedDoc.obligatorio || false,
              activo: true, // Asegurar que esté activo
              completed: false,
              asignadoPor: usuario.email || usuario.nombre,
              asignadoEl: serverTimestamp(),
            },
            { merge: true }
          );
          console.log(`✅ Documento ${selectedDoc.name} asignado automáticamente a ${dni}`);
          // ---------------------------------------------------------------------

          // Crear documento en Firestore
          const legajoCollectionRef = collection(
            db,
            "onboarding",
            dni,
            "legajo"
          );
          const docRef = await addDoc(legajoCollectionRef, {
            activo: true,
            ano: currentDate.getFullYear(),
            contador: 0,
            dni: dni,
            fechaDoc: serverTimestamp(),
            fechaVista: "",
            link: downloadURL,
            mes: String(currentDate.getMonth() + 1).padStart(2, "0"),
            nombre: titulo,
            ruc: ruc,
            tipo:
              selectedDoc.name === "Contrato de trabajo"
                ? "Contratos"
                : selectedDoc.name,
            procesadoPor: usuario.email || usuario.nombre,
            documentId: selectedDoc.originalId,
          });

          await setDoc(docRef, { id: docRef.id }, { merge: true });

          completedFiles++;
          setUploadProgress((completedFiles / totalFiles) * 100);
        } catch (error) {
          console.error(`Error al procesar archivo para DNI ${dni}:`, error);
          setErrors((prev) => [
            ...prev,
            `Error al procesar ${dni}: ${error.message}`,
          ]);
        }
      });

      await Promise.all(uploadPromises);

      // AUDITORÍA
      try {
        await logBusinessAction("CARGA_MASIVA_ONBOARDING", "GestionOnboarding", {
          cambiosRealizados: {
            cargaMasiva: {
              anterior: null,
              nuevo: `${completedFiles} documentos cargados`
            }
          },
          tipoDocumento: selectedDoc.name,
          cantidadDocumentos: completedFiles,
          totalSeleccionados: totalFiles
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de onboarding", e);
      }

      Swal.fire({
        title: "¡Éxito!",
        text: "Los documentos se han procesado correctamente",
        icon: "success",
      });

      setFiles([]);
      setSelectedFolder("");
      setErrors([]);
    } catch (error) {
      console.error("Error al procesar archivos:", error);
      Swal.fire({
        title: "Error",
        text: "Hubo un error al procesar los archivos",
        icon: "error",
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const selectedFolderData = folders.find(
    (folder) => folder.id === selectedFolder
  );

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderBottom: "3px solid #f39c12",
            }}
          >
            <div style={{position:"relative",display:"flex",alignItems:"center",justifyContent:"center"}} className="w-100">

              <div className="text-center">
                <h2 className="fw-bold mb-1">
                  <FaCloudUploadAlt className="me-3" />
                  Proceso de Onboarding
                </h2>
                <p className="mb-0 opacity-75">
                  Carga masiva de documentos para firma digital
                </p>
              </div>
              <div style={{position:"absolute",right:"20px"}}>
                <BotonManualHeader usuario={usuario} />
              </div>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Indicador de carga global */}
            {uploading && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                style={{
                  zIndex: 1080,
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  backdropFilter: "blur(5px)",
                }}
              >
                <Card
                  className="shadow-lg border-0 text-center"
                  style={{
                    borderRadius: "15px",
                    minWidth: "300px",
                    padding: "20px",
                  }}
                >
                  <Card.Body>
                    <Spinner
                      animation="border"
                      variant="primary"
                      style={{ width: "3rem", height: "3rem" }}
                    />
                    <h5 className="mt-3 mb-3">Procesando documentos...</h5>
                    <div
                      className="progress mb-3"
                      style={{ height: "12px", borderRadius: "10px" }}
                    >
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style={{
                          width: `${uploadProgress}%`,
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        }}
                        aria-valuenow={uploadProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        {Math.round(uploadProgress)}%
                      </div>
                    </div>
                    <p className="text-muted mb-0">
                      Procesando {files.length} documento
                      {files.length !== 1 ? "s" : ""}...
                    </p>
                  </Card.Body>
                </Card>
              </div>
            )}

            {/* Estado de carga inicial */}
            {loading && (
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="py-5">
                  <div className="text-center">
                    <Spinner
                      animation="border"
                      variant="primary"
                      style={{ width: "3rem", height: "3rem" }}
                    />
                    <h5 className="mt-3 text-muted">
                      Cargando tipos de documentos...
                    </h5>
                    <p className="text-muted">
                      Consultando la lista maestra de documentos
                    </p>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Error de carga */}
            {loadError && (
              <Alert
                variant="warning"
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "10px" }}
              >
                <div className="d-flex align-items-center">
                  <FaExclamationTriangle className="me-3 fs-4" />
                  <div>
                    <h6 className="fw-bold mb-1">
                      No se pudieron cargar los documentos
                    </h6>
                    <p className="mb-2">{loadError}</p>
                    <p className="mb-0">
                      Primero debe configurar documentos de tipo "firma digital"
                      en la{" "}
                      <Button
                        as="a"
                        href="/lista-maestra-documentos"
                        variant="link"
                        className="p-0 fw-bold text-decoration-none"
                      >
                        Lista Maestra de Documentos
                      </Button>
                    </p>
                  </div>
                </div>
              </Alert>
            )}

            {/* Selector de tipo de documento */}
            {!loading && !loadError && (
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header className="bg-light border-0 py-3">
                  <h5 className="mb-0 fw-semibold">
                    <FaFileSignature className="me-2 text-primary" />
                    Selección de Tipo de Documento
                  </h5>
                </Card.Header>
                <Card.Body className="p-4">
                  <Form.Group>
                    <Form.Label className="fw-semibold text-dark">
                      Tipo de Documento
                    </Form.Label>
                    <Form.Select
                      value={selectedFolder}
                      onChange={(e) => {
                        setSelectedFolder(e.target.value);
                        setFiles([]);
                        setErrors([]);
                      }}
                      className="form-control-lg"
                      style={{ borderRadius: "10px" }}
                    >
                      <option value="">Seleccione un tipo de documento</option>
                      {folders.map((folder) => (
                        <option key={folder.id} value={folder.id}>
                          {folder.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Solo se muestran documentos configurados para firma
                      digital
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>
            )}

            {/* Información del documento seleccionado */}
            {selectedFolderData && (
              <Card
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="p-4">
                  <Row className="align-items-center">
                    <Col xs="auto">
                      <IconWrapper type={selectedFolderData.type}>
                        {getIcon(selectedFolderData.icon)}
                      </IconWrapper>
                    </Col>
                    <Col>
                      <h4 className="mb-2 fw-bold">
                        {selectedFolderData.name}
                      </h4>
                      <p className="text-muted mb-3">
                        {selectedFolderData.description}
                      </p>
                      <div className="d-flex gap-2">
                        <Badge
                          bg="primary"
                          className="px-3 py-2"
                          style={{ borderRadius: "8px" }}
                        >
                          <FaFileSignature className="me-1" />
                          Requiere firma
                        </Badge>
                        {selectedFolderData.obligatorio && (
                          <Badge
                            bg="danger"
                            className="px-3 py-2"
                            style={{ borderRadius: "8px" }}
                          >
                            Obligatorio
                          </Badge>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* Sección de carga de archivos */}
            {selectedFolderData && (
              <Card
                className="border-0 shadow-sm"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header className="bg-primary text-white py-3">
                  <div className="d-flex align-items-center">
                    <FaUpload className="me-2" />
                    <div>
                      <h5 className="mb-0 fw-semibold">
                        Carga masiva de documentos
                      </h5>
                      <small className="opacity-75">
                        Suba múltiples documentos siguiendo el formato requerido
                      </small>
                    </div>
                  </div>
                </Card.Header>

                <Card.Body className="p-4">
                  {/* Mostrar errores */}
                  {errors.length > 0 && (
                    <Alert
                      variant="danger"
                      className="border-0 shadow-sm mb-4"
                      style={{ borderRadius: "10px" }}
                    >
                      <div className="d-flex align-items-start">
                        <FaExclamationTriangle className="me-3 mt-1" />
                        <div className="flex-grow-1">
                          <h6 className="fw-bold mb-2">
                            Se encontraron {errors.length} errores:
                          </h6>
                          <ul className="mb-0 ps-3">
                            {errors.map((error, index) => (
                              <li key={index} className="small mb-1">
                                {error}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => setErrors([])}
                          className="text-danger p-0"
                        >
                          <FaTimes />
                        </Button>
                      </div>
                    </Alert>
                  )}

                  {/* Campo de selección de archivos */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-dark">
                      <FaCloudUploadAlt className="me-2" />
                      Selección de Archivos
                    </Form.Label>
                    <Form.Control
                      type="file"
                      multiple
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="form-control-lg"
                      style={{
                        borderRadius: "10px",
                        border: "2px dashed #dee2e6",
                        padding: "20px",
                      }}
                    />
                    <Form.Text className="text-muted">
                      <strong>Formato requerido:</strong> DNI-TITULO.pdf
                      (ejemplo: 41607220-Contrato de trabajo.pdf)
                      <br />
                      Solo se permiten archivos PDF
                    </Form.Text>
                  </Form.Group>

                  {/* Lista de archivos seleccionados */}
                  {files.length > 0 && (
                    <Card
                      className="bg-light border-0 mb-4"
                      style={{ borderRadius: "10px" }}
                    >
                      <Card.Header className="bg-transparent border-0 py-3">
                        <h6 className="mb-0 fw-semibold">
                          <FaCheckCircle className="me-2 text-success" />
                          Archivos seleccionados ({files.length})
                        </h6>
                      </Card.Header>
                      <Card.Body className="p-3">
                        <div
                          className="bg-white rounded-3 p-3"
                          style={{ maxHeight: "300px", overflowY: "auto" }}
                        >
                          {files.map((file, index) => (
                            <div
                              key={index}
                              className="d-flex justify-content-between align-items-center p-3 border-bottom"
                            >
                              <div className="d-flex align-items-center">
                                <FaFileAlt
                                  className="text-primary me-3"
                                  size={16}
                                />
                                <div>
                                  <div className="fw-medium">{file.name}</div>
                                  <small className="text-muted">
                                    DNI: {file.dniInfo?.dni} | Título:{" "}
                                    {file.dniInfo?.titulo}
                                  </small>
                                </div>
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeFile(index)}
                                style={{ borderRadius: "6px" }}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Botón de procesamiento */}
                  <div className="d-flex justify-content-end pt-3 border-top">
                    <Button
                      variant="primary"
                      onClick={handleUpload}
                      disabled={
                        !selectedFolder || files.length === 0 || uploading
                      }
                      className="d-flex align-items-center px-4 py-3"
                      size="lg"
                      style={{
                        borderRadius: "10px",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        border: "none",
                      }}
                    >
                      {uploading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <FaUpload className="me-2" />
                          Procesar documentos
                        </>
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Estado inicial sin selección */}
            {!loading && !loadError && !selectedFolderData && (
              <Card
                className="shadow-sm border-0"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="py-5">
                  <div className="text-center">
                    <div
                      className="bg-light rounded-circle mx-auto mb-3"
                      style={{
                        width: "80px",
                        height: "80px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FaCloudUploadAlt size={32} className="text-muted" />
                    </div>
                    <h5 className="text-muted mb-2">
                      Seleccione un tipo de documento para comenzar
                    </h5>
                    <p className="text-muted">
                      Elija el tipo de documento que desea procesar para la
                      carga masiva
                    </p>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default Onboarding;


